import { z } from 'zod';
import zxcvbn from 'zxcvbn';

// Email validation schema with improved regex
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .regex(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    'Invalid email format'
  );

// Password validation schema with zxcvbn strength check
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters')
  .refine((password) => {
    const result = zxcvbn(password);
    return result.score >= 2;
  }, {
    message: 'Password is too weak. Include a mix of letters, numbers, and symbols.'
  });

// Full name validation schema
export const fullNameSchema = z
  .string()
  .min(2, 'Full name must be at least 2 characters')
  .max(100, 'Full name must not exceed 100 characters')
  .regex(
    /^[a-zA-Z\s'-]+$/,
    'Full name can only contain letters, spaces, hyphens, and apostrophes'
  );

// Sign up form validation schema
export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: fullNameSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Sign in form validation schema
export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
});

// Password reset request schema
export const resetPasswordRequestSchema = z.object({
  email: emailSchema,
});

// New password validation schema for password reset
export const resetPasswordSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Function to validate password strength
export function validatePassword(password: string): { 
  isValid: boolean; 
  score: number;
  feedback: string[];
} {
  const result = zxcvbn(password);
  const feedback = [
    ...(result.feedback.suggestions || []),
    result.feedback.warning || ''
  ].filter(Boolean);

  return {
    isValid: result.score >= 2,
    score: result.score,
    feedback
  };
}

// Function to validate email format
export function validateEmail(email: string): {
  isValid: boolean;
  errors: string[];
} {
  try {
    emailSchema.parse(email);
    return { isValid: true, errors: [] };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        isValid: false,
        errors: error.errors.map(e => e.message)
      };
    }
    return {
      isValid: false,
      errors: ['Invalid email format']
    };
  }
}